// ACTA_AGENT_DAILY_ACTIONS_V1
// A read-only projection of existing Commerce and GMAO data.
// No new task collection, mock records, or permissions are created.

function localDayKey(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return [year, month, day].join("-");
}

function openCommercialAction(row) {
  if (!row || !row.nextAction || !row.nextActionAt) return false;
  if (["completed", "cancelled", "superseded"].includes(row.nextActionStatus)) return false;
  if (["completed", "cancelled"].includes(row.followupStatus)) return false;
  if (["completed", "cancelled"].includes(row.actionStatus)) return false;
  if (row.meetingOutcomeCode) return false;
  const activeClient = row.lifecycleStage === "client" && row.clientStatus !== "inactive";
  if (row.commercialStatus === "lost" || (row.commercialStatus === "won" && !activeClient)) return false;
  return !row.nextActionStatus || row.nextActionStatus === "open";
}

function rowTitle(row) {
  return row.company || [row.firstName, row.lastName].filter(Boolean).join(" ") || "Contact";
}

export function buildDailyActionItems({ agenda = [], interventions = [], canOpenInstallations = false, now = new Date() } = {}) {
  const today = localDayKey(now);
  if (!today) return [];
  const items = [];

  for (const row of Array.isArray(agenda) ? agenda : []) {
    if (!openCommercialAction(row)) continue;
    const day = localDayKey(row.nextActionAt);
    if (!day || day > today) continue;
    const isMeeting = row.nextActionType === "meeting";
    const label = row.nextAction || (isMeeting ? "Rendez-vous" : "Action commerciale");
    const id = row.id || row.prospectId;
    const meetingId = row.appointmentId || row.meetingId || row.nextActionNoteId || id;
    const outcome = new Date(row.nextActionAt).getTime() < now.getTime() ? "outcome" : "edit";
    const meetingParams = new URLSearchParams({
      agentMeetingKey: String(meetingId || ""),
      agentMeetingAction: outcome,
    });
    // Opportunity ids are NOT prospect ids. Their workflow lives in the pipeline.
    const path = row.agendaSource === "opportunity"
      ? "/app/commerce/pipeline"
      : isMeeting && meetingId
        ? "/app/commerce/agenda?" + meetingParams.toString()
        : (row.prospectId || id)
          ? "/app/commerce/prospects/" + encodeURIComponent(row.prospectId || id) + "?tab=comments"
          : "/app/commerce/agenda";
    items.push({
      key: "commerce:" + String(row.nextActionNoteId || meetingId || id) + ":" + day,
      domain: "commercial",
      origin: "Agenda commercial",
      label,
      subject: rowTitle(row),
      type: isMeeting ? "meeting" : "action",
      actionType: row.nextActionType || "other",
      dueAt: row.nextActionAt,
      day,
      overdue: day < today,
      path,
      noteId: !isMeeting ? (row.nextActionNoteId || "") : "",
      ownerId: row.createdBy || "",
    });
  }

  for (const row of Array.isArray(interventions) ? interventions : []) {
    if (!row || !["planned", "confirmed", "in_progress"].includes(row.status) || row.planningHiddenAt) continue;
    const dueAt = row.scheduledDate || row.scheduledStart;
    const day = localDayKey(dueAt);
    if (!day || day > today) continue;
    if (!row.id) continue;
    const canOpenInstallation = canOpenInstallations && row.type === "installation" && row.installationId && row.status !== "cancelled";
    items.push({
      key: "gmao:" + row.id,
      domain: "gmao",
      origin: "Planning GMAO",
      label: row.title || (row.type === "installation" ? "Installation" : "Intervention"),
      subject: row.clientName || row.machineName || row.siteName || "Intervention",
      type: "intervention",
      actionType: row.type,
      dueAt,
      day,
      overdue: day < today,
      path: canOpenInstallation
        ? "/app/gmao/installations/" + encodeURIComponent(row.installationId)
        : "/app/gmao/interventions/" + encodeURIComponent(row.id),
      noteId: "",
      ownerId: row.assignedUserId || row.assignedTechnicianId || "",
    });
  }

  return items.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    return String(a.dueAt).localeCompare(String(b.dueAt));
  });
}

export function actionTimeLabel(item) {
  const value = item?.dueAt;
  if (!value || (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))) return "Sans horaire";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Sans horaire";
  return parsed.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
